/* eslint-disable react/jsx-key */
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasProductContext, withProductContext } from "@insite/client-framework/Components/ProductContext";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Icon, { IconPresentationProps } from "@insite/mobius/Icon";
import Tooltip from "@insite/mobius/Tooltip";
import Typography from "@insite/mobius/Typography";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import advancedLine from "@sonic/Assets/Icons/advancedLine";
import basicLine from "@sonic/Assets/Icons/basicLine";
import expertLine from "@sonic/Assets/Icons/expertLine";
import interMediateLine from "@sonic/Assets/Icons/interMediateLine";
import * as React from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

type OwnProps = WidgetProps & HasProductContext & ReturnType<typeof mapStateToProps>;

const mapStateToProps = (state: ApplicationState) => ({
    productSettings: getSettingsCollection(state).productSettings,
});

export interface ProductSpecsStyle {
    productDetailContainer?: InjectableCss;
    container?: InjectableCss;
    smallContainer?: InjectableCss;
    icon?: IconPresentationProps;
    iconRight?: IconPresentationProps;
}

export const productLineDisplayStyle: ProductSpecsStyle = {
    productDetailContainer: {
        css: css`
            padding-bottom: 10px;
            border-bottom: 1px solid #00000026;
        `,
    },
    container: {
        css: css`
            width: 100%;
            max-width: 50%;
            margin-left: auto;
            display: flex;
            height: 48px;
            padding: 6px 12px;
            border: 1px solid #dedede;
            border-radius: 3px;
            align-items: center;
        `,
    },
    smallContainer: {
        css: css`
            display: flex;
            align-items: center;
            justify-content: flex-end;
            text-align: right;
            gap: 10px;
            ${({ theme }: { theme: BaseTheme }) => {
                return breakpointMediaQueries(theme, [
                    css`
                        justify-content: flex-end;
                    `,
                    css`
                        justify-content: flex-end;
                    `,
                    css`
                        justify-content: flex-start;
                    `,
                    css`
                        justify-content: flex-start;
                    `,
                    css`
                        justify-content: flex-start;
                    `,
                ]);
            }}
        `,
    },
    icon: {
        css: css`
            width: 45px;
            height: 34px;

            ${({ theme }: { theme: BaseTheme }) => {
                return breakpointMediaQueries(theme, [
                    css`
                        width: 35px;
                    `,
                    css`
                        width: 35px;
                    `,
                    css`
                        width: 45px;
                    `,
                    css`
                        width: 45px;
                    `,
                    css`
                        width: 45px;
                    `,
                ]);
            }}
        `,
        size: 34,
    },
    iconRight: {
        css: css`
            width: 45px;
            height: 34px;
            justify-content: flex-end;
            ${({ theme }: { theme: BaseTheme }) => {
                return breakpointMediaQueries(theme, [
                    css`
                        width: 35px;
                    `,
                    css`
                        width: 35px;
                    `,
                    css`
                        width: 45px;
                    `,
                    css`
                        width: 45px;
                    `,
                    css`
                        width: 45px;
                    `,
                ]);
            }}
        `,
        size: 34,
    },
};

const ProductLineDisplay: React.FC<OwnProps> = props => {
    const {
        productContext: { product },
    } = props;
    const tooltipText = translate(
        "Expertise Level is an indicator of the necessary familiarity you should have with the subject matter (Basic, Beginner, Intermediate and Advanced)",
    );
    const tooltipStyle = {
        tooltipWrapper: css`
            margin-left: auto;
            align-self: flex-start;
            margin-top: -2px;
        `,
    };

    if (product.productLine === null) {
        return null;
    }

    let iconLine = basicLine;
    switch (product.productLine.name) {
        case "Extreme": {
            iconLine = expertLine;
            break;
        }
        case "Advanced": {
            iconLine = advancedLine;
            break;
        }
        case "Intermediate": {
            iconLine = interMediateLine;
            break;
        }
        case "Basic": {
            iconLine = basicLine;
            break;
        }
        default:
            break;
    }

    return (
        <StyledWrapper {...productLineDisplayStyle.productDetailContainer}>
            <StyledWrapper {...productLineDisplayStyle.container}>
                <Icon {...productLineDisplayStyle.icon} src={iconLine} />
                <Typography variant="headerSecondary">{product.productLine.name}</Typography>
                <Tooltip
                    cssOverrides={{ tooltipWrapper: tooltipStyle.tooltipWrapper }}
                    typographyProps={{ weight: 400 }}
                    iconProps={{ color: "#DEDEDE" }}
                    text={tooltipText}
                />
            </StyledWrapper>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(withProductContext(ProductLineDisplay)),
    definition: {
        group: "Product Details",
        icon: "Product detail",
    },
};

export default widgetModule;
